import * as React from "react";
import "./styles.scss";
import {
  CalendarEvent,
  CalendarEventFilters,
  Post,
  SocialNetworkPosted,
  SocialNetworksId,
  TimePeriod,
} from "@/interfaces";
import { EventTile } from "./components/EventTile";
import { useEffect, useRef, useState } from "react";
import { HorizontalScrollContainer } from "../HorizontalScrollContainer";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { DatesSetArg, EventClickArg } from "@fullcalendar/core";
import { EventModal } from "./components/EventModal";
import { EventFilters } from "./components/EventFilters";
import { isEqual } from "lodash";
import { RouteComponentProps } from "react-router-dom";
import { PageWrapper } from "@/components/PageWrapper";
import { CoachService } from "@/services";
import { PostDetailsDialog } from "@/components";
import { useCalendarEvents } from "@/utils";

interface PathProps {
  coachId?: string;
}
interface CalendarPageProps extends RouteComponentProps<PathProps> {
  pageLoading?: boolean;
}

const EMPTY_CALENDAR_EVENTS: CalendarEvent[] = [];

export const CalendarPage: React.FC<CalendarPageProps> = ({ pageLoading, match: { params } }) => {
  const [title, setTitle] = React.useState<string>("Calendar");
  const [postId, setPostId] = React.useState<number>();
  const [socialNetworkId, setSocialNetworkId] = React.useState<SocialNetworksId>();
  const [isPostModalOpen, setIsPostModalOpen] = React.useState<boolean>(false);

  let coachId: number | undefined = Number(params.coachId);
  if (Number.isNaN(coachId)) coachId = undefined;

  useEffect(() => {
    if (coachId) {
      CoachService.getById(coachId.toString())
        .then(response => {
          let coachName = response.coach?.companyName || response.profile?.fullName || "Coach";
          setTitle(`Calendar for ${coachName}`);
        })
        .catch(e => {
          console.error(e);
        });
    }
  }, [coachId]);

  const searchParams = new URLSearchParams(location.search);
  type ViewType = "dayGridWeek" | "dayGridMonth";
  const viewParam: ViewType = searchParams.get("view")?.toLowerCase() === "week" ? "dayGridWeek" : "dayGridMonth";

  let today = new Date();
  const dateRange = {
    start: new Date(2020, 0, 1),
    end: new Date(today.getFullYear() + 2, today.getMonth(), today.getDay()),
  };
  let dateParam = searchParams.get("date") ? new Date(Number(searchParams.get("date"))) : today;
  if (dateParam.toString() === "Invalid Date") dateParam = today;
  else if (dateParam < dateRange.start) dateParam = dateRange.start;
  else if (dateParam > dateRange.end) dateParam = dateRange.end;

  const calendarRef = useRef<FullCalendar | null>(null);
  const [filteredEvents, setFilteredEvents] = useState<{ eventObject: CalendarEvent; title: string; date: string }[]>(
    []
  );
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [dateTime, setDateTime] = useState<Date>(dateParam);
  const [view, setView] = useState<ViewType>(viewParam);
  const [eventFilters, setEventFilters] = useState<CalendarEventFilters>({
    sourceTypes: [],
    playerIds: [],
    branchIds: [],
    regionIds: [],
    socialNetworkIds: [],
    statuses: [],
  });

  const timePeriod: TimePeriod = view === "dayGridMonth" ? "MONTH" : "WEEK";
  const {
    data: events = EMPTY_CALENDAR_EVENTS,
    isLoading,
    refetch,
  } = useCalendarEvents(dateTime, timePeriod, coachId, eventFilters.playerIds);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", view === "dayGridWeek" ? "week" : "month");
    url.searchParams.set("date", `${+dateTime}`);
    window.history.pushState({}, "", url);
  }, [dateTime, view]);

  const renderEventContent = (event: any) => {
    return <EventTile event={event.event.extendedProps.eventObject} dense={event.view.type === "dayGridMonth"} />;
  };

  const handleEventFiltersChanged = (newEventFilters: CalendarEventFilters) => {
    if (!isEqual(eventFilters, newEventFilters)) setEventFilters(newEventFilters);
  };

  useEffect(() => {
    const filteredEvents = events.filter(event => {
      if (eventFilters.sourceTypes.length > 0 && !eventFilters.sourceTypes.includes(event.sourceType)) return false;
      if (eventFilters.playerIds.length > 0 && !event.playerIds.some(el => eventFilters.playerIds.includes(el)))
        return false;
      if (eventFilters.regionIds.length > 0 && !event.regionIds.some(el => eventFilters.regionIds.includes(el)))
        return false;
      if (eventFilters.branchIds.length > 0 && !event.branchIds.some(el => eventFilters.branchIds.includes(el)))
        return false;
      if (
        eventFilters.socialNetworkIds.length > 0 &&
        !event.socialNetworkIds.some(el => eventFilters.socialNetworkIds.includes(el))
      )
        return false;
      if (eventFilters.statuses.length > 0 && !eventFilters.statuses.some(el => event.postStatuses.includes(el)))
        return false;
      return true;
    });
    const nextFilteredEvents = filteredEvents.map(e => ({
      eventObject: e,
      title: e.sourceTitle,
      date: e.scheduledDateTime,
    }));
    setFilteredEvents(prev => (isEqual(prev, nextFilteredEvents) ? prev : nextFilteredEvents));
  }, [eventFilters, events]);

  const handleDatesSet = (event: DatesSetArg) => {
    const newEventFilters: CalendarEventFilters = { ...eventFilters };
    setView(event.view.type as ViewType);
    if (calendarRef.current) {
      const nextDate = calendarRef.current.getApi().getDate();
      setDateTime(prev => (prev.getTime() === nextDate.getTime() ? prev : nextDate));
    }
    handleEventFiltersChanged(newEventFilters);
  };

  const handleNavLinkDayClick = (date: Date, _event: UIEvent) => {
    if (view !== "dayGridWeek" && calendarRef.current) {
      calendarRef.current.getApi().changeView("dayGridWeek", date);
    }
  };

  const handleEventClick = (event: EventClickArg) => {
    const eventObject = event.event.extendedProps.eventObject;
    setSelectedEvent(event.event.extendedProps.eventObject);
  };

  const handlePost = (post: Post, socialPost?: SocialNetworkPosted) => {
    setSelectedEvent(null);
    setPostId(post.postId);
    setSocialNetworkId(socialPost?.socialNetworkId);
    setIsPostModalOpen(true);
  };

  const handlePostModalClosed = () => {
    setPostId(undefined);
    setSocialNetworkId(undefined);
    setIsPostModalOpen(false);
  };

  return (
    <PageWrapper title={title} hideSeparator loading={!!pageLoading}>
      <EventFilters eventFilters={eventFilters} isLoading={isLoading} onFiltersChanged={handleEventFiltersChanged} />
      <HorizontalScrollContainer>
        <FullCalendar
          viewClassNames={isLoading ? "calendar-loading" : "calendar-loaded"}
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          events={filteredEvents}
          initialView={view}
          fixedWeekCount={false}
          initialDate={dateTime}
          height="auto"
          buttonText={{ today: "Today", month: "Month", week: "Week" }}
          headerToolbar={{ start: "prev,today,next", center: "title", end: "dayGridMonth,dayGridWeek" }}
          dayMaxEventRows={view === "dayGridMonth" ? 5 : undefined}
          navLinks={view === "dayGridMonth"}
          navLinkDayClick={handleNavLinkDayClick}
          eventContent={renderEventContent}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
        />
      </HorizontalScrollContainer>
      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onUpdate={() => refetch()}
          handlePost={handlePost}
        />
      )}

      {isPostModalOpen && postId && (
        <PostDetailsDialog
          open={isPostModalOpen}
          postId={postId}
          socialNetworkId={socialNetworkId}
          onClose={handlePostModalClosed}
        />
      )}
    </PageWrapper>
  );
};
